import React, { useEffect } from 'react';
import { StyleSheet, Animated } from 'react-native';
import { Platform } from 'react-native';

import { useTheme } from '@/components/theme-provider';
import { Button } from '@/components/ui';
import { Text } from '@/components/ui';
import type { NotificationItem } from '@/utils/notification-manager';

interface ToastNotificationProps {
	notification: NotificationItem;
	onDismiss: (id: string) => void;
}

export const ToastNotification: React.FC<ToastNotificationProps> = ({
	notification,
	onDismiss,
}) => {
	const theme = useTheme();
	const translateY = React.useRef(new Animated.Value(100)).current;

	useEffect(() => {
		// Animate in
		Animated.timing(translateY, {
			toValue: 0,
			duration: 300,
			useNativeDriver: true,
		}).start();

		// Auto-dismiss after duration
		if (notification.options.duration) {
			const timer = setTimeout(() => {
				onDismiss(notification.id);
			}, notification.options.duration);

			return () => clearTimeout(timer);
		}
	}, [notification.id, notification.options.duration, onDismiss, translateY]);

	const handleDismiss = () => {
		Animated.timing(translateY, {
			toValue: 100,
			duration: 300,
			useNativeDriver: true,
		}).start(() => {
			onDismiss(notification.id);
		});
	};

	const getVariantStyle = () => {
		switch (notification.variant) {
			case 'success':
				return { backgroundColor: theme.colors.primary };
			case 'error':
				return { backgroundColor: theme.colors.error };
			case 'warning':
				return { backgroundColor: theme.colors.tertiary };
			case 'info':
			default:
				return { backgroundColor: theme.colors.surfaceVariant };
		}
	};

	const getTextColor = () => {
		switch (notification.variant) {
			case 'success':
			case 'error':
			case 'warning':
				return theme.colors.onPrimary;
			case 'info':
			default:
				return theme.colors.onSurfaceVariant;
		}
	};

	return (
		<Animated.View
			style={[
				styles.container,
				Platform.OS === 'web' ? styles.webContainer : styles.mobileContainer,
				getVariantStyle(),
				{ transform: [{ translateY }] },
			]}
		>
			<Text style={[styles.message, { color: getTextColor() }]}>
				{notification.message}
			</Text>
			{notification.actions && notification.actions.length > 0 && (
				<Button
					mode="text"
					onPress={notification.actions[0].onPress}
					textColor={getTextColor()}
					compact
				>
					{notification.actions[0].label}
				</Button>
			)}
			<Button
				mode="text"
				onPress={handleDismiss}
				textColor={getTextColor()}
				compact
			>
				×
			</Button>
		</Animated.View>
	);
};

const styles = StyleSheet.create({
	container: {
		flexDirection: 'row',
		alignItems: 'center',
		padding: 16,
		borderRadius: 8,
		elevation: 6,
		zIndex: 1000,
		margin: 16,
	},
	mobileContainer: {
		position: 'absolute',
		bottom: 0,
		left: 0,
		right: 0,
	},
	webContainer: {
		position: 'fixed',
		top: 20,
		right: 20,
		maxWidth: 400,
		minWidth: 300,
	},
	message: {
		flex: 1,
		fontSize: 16,
	},
});
